import { useEffect, useMemo, useState } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { api } from '../api';
import type { Tenant } from '../types';
import { Modal } from '../components/Modal';
import { TenantForm } from '../components/TenantForm';

const helper = createColumnHelper<Tenant>();

export const TenantsPage = () => {
  const [rows, setRows] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [insertOpen, setInsertOpen] = useState(false);
  const [editing, setEditing] = useState<Tenant | null>(null);

  const load = async () => {
    setLoading(true);
    setRows(await api.tenants());
    setLoading(false);
  };
  useEffect(() => {
    void load();
  }, []);

  const columns = useMemo(
    () => [
      helper.accessor('id', { header: 'ID', size: 60 }),
      helper.accessor('companyName', { header: 'Company' }),
      helper.accessor('tradingName', { header: 'Trading name' }),
      helper.accessor('contactPerson', { header: 'Contact' }),
      helper.accessor('contactEmail', { header: 'Email' }),
      helper.accessor('contactNumber', { header: 'Phone' }),
      helper.accessor('tpin', { header: 'TPIN' }),
      helper.accessor('companyRegistration', { header: 'Reg' }),
      helper.display({
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={() => setEditing(row.original)}>Edit</button>
            <button
              className="danger"
              onClick={async () => {
                if (!confirm(`Delete ${row.original.companyName}?`)) return;
                try {
                  await api.deleteTenant(row.original.id);
                  await load();
                } catch (e) {
                  alert(e instanceof Error ? e.message : 'delete failed');
                }
              }}
            >
              Delete
            </button>
          </div>
        ),
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, globalFilter: search },
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearch,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: 'includesString',
  });

  return (
    <div className="page">
      <h1>Tenants</h1>
      <div className="toolbar">
        <input
          className="search"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="spacer" />
        <button className="primary" onClick={() => setInsertOpen(true)}>
          + Insert tenant
        </button>
      </div>
      {loading ? (
        <div>Loading…</div>
      ) : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th key={h.id} onClick={h.column.getToggleSortingHandler()}>
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {h.column.getIsSorted() === 'asc'
                        ? ' ▲'
                        : h.column.getIsSorted() === 'desc'
                          ? ' ▼'
                          : ''}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((r) => (
                <tr key={r.id}>
                  {r.getVisibleCells().map((c) => (
                    <td key={c.id}>{flexRender(c.column.columnDef.cell, c.getContext())}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {insertOpen && (
        <Modal title="Insert tenant" onClose={() => setInsertOpen(false)}>
          <TenantForm
            save={(input, force) => api.createTenant(input, force)}
            onSuccess={async () => {
              setInsertOpen(false);
              await load();
            }}
            onCancel={() => setInsertOpen(false)}
            submitLabel="Create"
          />
        </Modal>
      )}
      {editing && (
        <Modal title={`Edit ${editing.companyName}`} onClose={() => setEditing(null)}>
          <TenantForm
            initial={editing}
            save={(input, force) => api.updateTenant(editing.id, input, force)}
            onSuccess={async () => {
              setEditing(null);
              await load();
            }}
            onCancel={() => setEditing(null)}
            submitLabel="Save"
          />
        </Modal>
      )}
    </div>
  );
};
